import React, { useState, useEffect, useCallback } from 'react';
import convert from 'convert-units';

const UnitConverter = () => {
  const [selectedCategory, setSelectedCategory] = useState('length');
  const [inputValue, setInputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('m');
  const [toUnit, setToUnit] = useState('ft');
  const [result, setResult] = useState('');

  // Get available measures and their display names
  const measureDisplayNames = {
    length: 'Length',
    area: 'Area',
    mass: 'Mass/Weight',
    volume: 'Volume',
    temperature: 'Temperature',
    time: 'Time',
    angle: 'Angle',
    speed: 'Speed',
    pressure: 'Pressure',
    energy: 'Energy',
    power: 'Power',
    digital: 'Digital Storage',
    frequency: 'Frequency',
    current: 'Electric Current',
    voltage: 'Voltage',
    each: 'Count/Quantity',
    partsPer: 'Parts Per',
    pace: 'Pace',
    volumeFlowRate: 'Volume Flow Rate',
    illuminance: 'Illuminance',
    reactivePower: 'Reactive Power',
    apparentPower: 'Apparent Power',
    reactiveEnergy: 'Reactive Energy'
  };

  const availableMeasures = convert().measures().filter(measure =>
    measureDisplayNames[measure]
  );

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    const units = convert().possibilities(category);
    setFromUnit(units[0]);
    setToUnit(units[1] || units[0]);
    setInputValue('');
    setResult('');
  };

  const handleConvert = useCallback(() => {
    if (!inputValue || isNaN(inputValue)) {
      setResult('Please enter a valid number');
      return;
    }

    try {
      const numValue = parseFloat(inputValue);
      const convertedValue = convert(numValue).from(fromUnit).to(toUnit);

      // Format the result
      let formattedResult;
      if (Math.abs(convertedValue) >= 1000000 || (Math.abs(convertedValue) < 0.0001 && convertedValue !== 0)) {
        formattedResult = convertedValue.toExponential(6);
      } else {
        formattedResult = parseFloat(convertedValue.toFixed(8)).toString();
      }

      const fromDesc = convert().describe(fromUnit);
      const toDesc = convert().describe(toUnit);

      setResult(`${inputValue} ${fromDesc.plural} = ${formattedResult} ${toDesc.plural}`);
    } catch (error) {
      setResult('Conversion error: ' + error.message);
    }
  }, [inputValue, fromUnit, toUnit]);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    setResult('');
  };

  // Get units for current category
  const currentUnits = convert().possibilities(selectedCategory);

  // Get unit descriptions for display
  const getUnitDisplay = (unitCode) => {
    const desc = convert().describe(unitCode);
    return `${desc.singular} (${unitCode})`;
  };

  // Auto-convert when input changes (real-time conversion)
  useEffect(() => {
    if (inputValue && !isNaN(inputValue) && fromUnit && toUnit) {
      handleConvert();
    }
  }, [inputValue, fromUnit, toUnit, handleConvert]);

  return (
    <div className="unit-converter">
      <h1>Unit Converter</h1>
      <p className="subtitle">Convert between different units of measurement</p>

      <div className="category-selector">
        <label htmlFor="category">Category:</label>
        <select
          id="category"
          value={selectedCategory}
          onChange={(e) => handleCategoryChange(e.target.value)}
        >
          {availableMeasures.map(measure => (
            <option key={measure} value={measure}>
              {measureDisplayNames[measure]}
            </option>
          ))}
        </select>
      </div>

      <div className="conversion-form">
        <div className="input-section">
          <label htmlFor="input-value">Value:</label>
          <input
            id="input-value"
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            placeholder="Enter value to convert"
            step="any"
          />
        </div>

        <div className="unit-selectors">
          <div className="from-unit">
            <label htmlFor="from-unit">From:</label>
            <select
              id="from-unit"
              value={fromUnit}
              onChange={(e) => setFromUnit(e.target.value)}
            >
              {currentUnits.map(unit => (
                <option key={unit} value={unit}>
                  {getUnitDisplay(unit)}
                </option>
              ))}
            </select>
          </div>

          <div className="to-unit">
            <label htmlFor="to-unit">To:</label>
            <select
              id="to-unit"
              value={toUnit}
              onChange={(e) => setToUnit(e.target.value)}
            >
              {currentUnits.map(unit => (
                <option key={unit} value={unit}>
                  {getUnitDisplay(unit)}
                </option>
              ))}
            </select>
          </div>
        </div>

      </div>

      {result && (
        <div className="result">
          <h3>Result:</h3>
          <p>{result}</p>
        </div>
      )}

      <div className="quick-examples">
        <h4>Quick Examples:</h4>
        <ul>
          <li>Length: 1 meter = 3.28084 feet</li>
          <li>Temperature: 100°C = 212°F</li>
          <li>Mass: 1 kilogram = 2.20462 pounds</li>
          <li>Volume: 1 liter = 0.264172 gallons</li>
          <li>Speed: 100 km/h = 62.1371 mph</li>
          <li>Pressure: 1 bar = 14.5038 psi</li>
        </ul>
      </div>

      <div className="features">
        <h4>Supported Conversions:</h4>
        <p>
          This converter supports {availableMeasures.length} different categories including
          length, area, mass, volume, temperature, time, speed, pressure, energy,
          power, digital storage, frequency, electrical units, and more.
        </p>
      </div>
    </div>
  );
};

export default UnitConverter;